import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import {
  getCMakeListsText,
  getPackageLanguages,
  getPackageXmlText,
  getSetupCfgText,
  getSetupPyText,
  initROSPackage
} from './initPackage';
import { toCamelCase } from '@/modules/globalModule/components/helperMethods';
import { Node } from '../modules/nodesCanvasModule/components/storeClasses/node';
import { createSrvFiles, getSrvText } from './srvFileGen';
import { toSnakeCase } from '@/modules/globalModule/components/helperMethods';
import { V4UUID, workspaceState } from '@/firebase/database';
import { getAllPackagesInWorkspace } from '@/firebase/databaseDependencyInjectables';
import { NodeGenStrategy } from './nodeGenStrategy';
import { PyNodeGenStrategy } from './pyNodeGenStrategy';
import { CppNodeGenStrategy } from './cppNodeGenStrategy';
import { generateMessageFiles, generateMessageText } from './msgFileGen';
import {
  generatePackageLaunchFile,
  generatePackageLaunchFileString
} from './launchFileGen';
import { LaunchFile } from '@/modules/controlCenterModule/components/robotConnection/loadLaunchFilesStrategy';

/**
 * Uses the details of the workspace stored in the database
 * to convert that workspace into a section of ROS packages.
 * Downloads that workspace of ROS packages as a zip file
 * @param workspaceId
 */
export function workspaceToZip(workspaceId: V4UUID) {
  let zip: JSZip = new JSZip();
  let state = workspaceState(workspaceId);
  // getting packages
  let workspaceName: string = state.info.name;
  let packages: Array<PackageT> = Object.entries(
    getAllPackagesInWorkspace(workspaceId) || {}
  ).map((packageData) => packageData[1]);
  if (!packages) packages = [];
  for (let pkg of packages) {
    if (pkg.robotPackage) continue;
    let pkgZip = zip.folder(toSnakeCase(pkg.name));
    if (!pkgZip) continue;
    generatePackageLaunchFile(pkgZip, pkg);
    initROSPackage(pkgZip, workspaceId, pkg);
    createSrvFiles(pkgZip, workspaceId, pkg);
    generateMessageFiles(pkgZip, workspaceId, pkg);
    generatePackageNodes(pkgZip, workspaceId, pkg);
  }
  zip.generateAsync({ type: 'blob' }).then(function (content) {
    FileSaver.saveAs(content, `${toCamelCase(workspaceName)}.zip`);
  });
}

const generatePackageNodes = (
  pkgZip: JSZip,
  workspaceId: V4UUID,
  pkg: PackageT
) => {
  pkg.nodes &&
    pkg.nodes.forEach((node: Node) => {
      let codeGenStrat: NodeGenStrategy = new CppNodeGenStrategy(
        node,
        workspaceId
      );
      if (node.language === 'py') {
        codeGenStrat = new PyNodeGenStrategy(node, workspaceId);
        // switching to python folder
        codeGenStrat.generateCodeZip(
          pkgZip.folder(toSnakeCase(pkg.name)) || pkgZip
        );
      } else {
        codeGenStrat.generateCodeZip(pkgZip);
      }
    });
};

export interface ITreeObject {
  type: string; // either file or folder
  name: string;
  path?: string;
}

export interface ITreeFile extends ITreeObject {
  data: string;
  symbol: string;
  language: string;
  elemId: V4UUID; // Id of element this file represents.
  // This can be node, msg or srv, or package if base package file
  // This information can be found based off the files name
}

export interface ITreeFolder extends ITreeObject {
  children: ITreeObject[];
}

/**
 * turns the current package into a filetree structure
 * @param workspaceId
 * @returns
 */
export const workspaceToFileTree = (
  workspaceId: V4UUID,
  packages: Record<string, PackageT> | undefined = undefined
): ITreeFolder | undefined => {
  if (!packages) {
    packages = getAllPackagesInWorkspace(workspaceId);
  }
  const wsState = workspaceState(workspaceId);
  if (!packages || !wsState || !wsState.info) return;
  // formatting data into file structure
  let rootFolder: ITreeFolder = {
    type: 'folder',
    name: toCamelCase(wsState.info.name),
    children: [],
    path: toCamelCase(wsState.info.name)
  };
  for (let pkg of Object.entries(packages).map((entry) => entry[1])) {
    if (pkg.robotPackage) continue;

    // base package folder
    const pkgTreeFolder: ITreeFolder = {
      type: 'folder',
      name: toCamelCase(pkg.name),
      children: [],
      path: `${rootFolder.path}/${toCamelCase(pkg.name)}`
    };
    rootFolder.children.push(pkgTreeFolder);

    // creating launch file
    const pkgLaunchFolder: ITreeFolder = {
      type: 'folder',
      name: 'launch',
      children: [
        {
          type: 'file',
          name: `${toSnakeCase(pkg.name)}.launch.py`,
          data: generatePackageLaunchFileString(pkg),
          symbol: '',
          language: 'python',
          elemId: pkg.id,
          path: `${pkgTreeFolder.path}/launch/${toSnakeCase(
            pkg.name
          )}.launch.py`
        } as ITreeFile
      ],
      path: `${pkgTreeFolder.path}/launch`
    };
    pkgTreeFolder.children.push(pkgLaunchFolder);

    // creating packageXML
    pkgTreeFolder.children.push({
      type: 'file',
      name: 'package.xml',
      data: getPackageXmlText(pkg, workspaceId),
      symbol: '',
      language: 'xml',
      elemId: pkg.id,
      path: `${pkgTreeFolder.path}/package.xml`
    } as ITreeFile);

    // src and include folder to add if package includes cpp nodes
    const srcFolder = {
      type: 'folder',
      name: 'src',
      children: [],
      path: `${pkgTreeFolder.path}/src`
    } as ITreeFolder;
    const includeFolder = {
      type: 'folder',
      name: toSnakeCase(pkg.name),
      children: [],
      path: `${pkgTreeFolder.path}/include/include`
    } as ITreeFolder;

    const language = getPackageLanguages(pkg);
    // create setup.py && setup.cfg
    if (language === 1) {
      pkgTreeFolder.children.push({
        type: 'file',
        name: 'setup.py',
        data: getSetupPyText(pkg),
        symbol: '',
        language: 'python',
        elemId: pkg.id,
        path: `${pkgTreeFolder.path}/setup.py`
      } as ITreeFile);
      pkgTreeFolder.children.push({
        type: 'file',
        name: 'setup.cfg',
        data: getSetupCfgText(pkg),
        symbol: '',
        language: 'cfg',
        elemId: pkg.id,
        path: `${pkgTreeFolder.path}/setup.cfg`
      } as ITreeFile);
    } else {
      // creating CMakeLists
      pkgTreeFolder.children.push({
        type: 'file',
        name: 'CMakeLists.txt',
        data: getCMakeListsText(pkg, workspaceId),
        symbol: '',
        language: 'cmake',
        elemId: pkg.id,
        path: `${pkgTreeFolder.path}/CMakeLists.txt`
      } as ITreeFile);
      pkgTreeFolder.children.push(srcFolder);
      pkgTreeFolder.children.push({
        type: 'folder',
        name: 'include',
        children: [includeFolder],
        path: `${pkgTreeFolder.path}/include`
      } as ITreeFolder);
    }

    // adding python and resource folder if pkg includes python nodes
    const pythonFolder = {
      type: 'folder',
      name: toSnakeCase(pkg.name),
      path: `${pkgTreeFolder.path}/${toSnakeCase(pkg.name)}`,
      children: [
        {
          type: 'file',
          name: '__init__.py',
          data: '',
          language: 'python',
          symbol: '',
          elemId: pkg.id,
          path: `${pkgTreeFolder.path}/${toSnakeCase(pkg.name)}/__init__.py`
        } as ITreeFile
      ]
    } as ITreeFolder;
    if (language !== 2) {
      // node includes python nodes
      // adding python and resource folder
      pkgTreeFolder.children.push(pythonFolder);
      pkgTreeFolder.children.push({
        type: 'folder',
        name: 'resource',
        path: `${pkgTreeFolder.path}/resource`,
        children: [
          {
            type: 'file',
            name: toSnakeCase(pkg.name),
            data: '',
            symbol: '',
            language: '',
            elemId: pkg.id,
            path: `${pkgTreeFolder.path}/resource/${toSnakeCase(pkg.name)}`
          } as ITreeFile
        ]
      } as ITreeFolder);
    }

    // generating srv files
    if (pkg.services && pkg.services.length > 0) {
      const srvFolder: ITreeFolder = {
        type: 'folder',
        name: 'srv',
        path: `${pkgTreeFolder.path}/srv`,
        children: pkg.services.map((srv) => {
          return {
            type: 'file',
            name: `${toCamelCase(srv.name)}.srv`,
            data: getSrvText(srv, pkg, workspaceId!),
            symbol: '',
            language: '',
            elemId: srv.id,
            path: `${pkgTreeFolder.path}/srv/${toCamelCase(srv.name)}.srv`
          } as ITreeFile;
        })
      };
      pkgTreeFolder.children.push(srvFolder);
    }

    // generating msg files
    if (pkg.msgs && pkg.msgs.length > 0) {
      const msgFolder: ITreeFolder = {
        type: 'folder',
        name: 'msg',
        path: `${pkgTreeFolder.path}/msg`,
        children: pkg.msgs.map((msg) => {
          return {
            type: 'file',
            name: `${toCamelCase(msg.name)}.msg`,
            data: generateMessageText(pkg.id, workspaceId!, msg),
            symbol: '',
            language: '',
            elemId: msg.id,
            path: `${pkgTreeFolder.path}/msg/${toCamelCase(msg.name)}.msg`
          } as ITreeFile;
        })
      };
      pkgTreeFolder.children.push(msgFolder);
    }

    // generating node code
    for (let node of pkg.nodes) {
      if (node.language === 'py') {
        let codeGenStrat = new PyNodeGenStrategy(node, workspaceId);
        pythonFolder.children.push({
          type: 'file',
          name: `${toCamelCase(node.name)}.py`,
          data: codeGenStrat.getPythonCodeString(),
          symbol: '',
          language: 'python',
          elemId: node.id,
          path: `${pythonFolder.path}/${toCamelCase(node.name)}.py`
        } as ITreeFile);
      } else {
        let codeGenStrat = new CppNodeGenStrategy(node, workspaceId);
        srcFolder.children.push({
          type: 'file',
          name: `${toCamelCase(node.name)}.cpp`,
          data: codeGenStrat.generateCppFileString(),
          symbol: '',
          language: 'cpp',
          elemId: node.id,
          path: `${srcFolder.path}/${toCamelCase(node.name)}.cpp`
        } as ITreeFile);
        includeFolder.children.push({
          type: 'file',
          name: `${toCamelCase(node.name)}.hpp`,
          data: codeGenStrat.generateHFileString(),
          symbol: '',
          language: 'cpp',
          elemId: node.id,
          path: `${includeFolder.path}/${toCamelCase(node.name)}.hpp`
        } as ITreeFile);
      }
    }
  }
  return rootFolder;
};

/**
 * Generates a list of launch file packages
 * @param workspaceId
 * @param workspacePath
 * @param packages
 * @returns
 */
export const generateWorkspaceLaunchFiles = (
  workspaceId: V4UUID,
  workspacePath: string,
  packages: Record<string, PackageT> | undefined = undefined
): LaunchFile[] => {
  if (!packages) {
    packages = getAllPackagesInWorkspace(workspaceId);
  }
  const wsState = workspaceState(workspaceId);
  if (!packages || !wsState || !wsState.info) return [];
  // formatting data into file structure
  let launchFiles: LaunchFile[] = [];
  for (let pkg of Object.entries(packages).map((entry) => entry[1])) {
    launchFiles.push({
      name: `${toSnakeCase(pkg.name)}.launch.py`,
      text: generatePackageLaunchFileString(pkg),
      pkgName: toSnakeCase(pkg.name),
      path: `${workspacePath}/src/${wsState.info?.name}/${toSnakeCase(
        pkg.name
      )}/launch/${toSnakeCase(pkg.name)}.launch.py`,
      pkgId: pkg.id
    });
  }
  return launchFiles;
};

/**
 * gets the path of a given file within the given filetree.
 * Path seperated by /, includes searched files name
 * @param fileTree - filetree to search in
 * @param fileName - file to search for
 * @returns
 */
export const getFilePath = (
  fileTree: ITreeObject,
  file: ITreeObject
): string | undefined => {
  if (file.path) return file.path;
  if (fileTree.name === file.name && fileTree.type === file.type)
    return file.name;
  const folder = fileTree as ITreeFolder;
  if (!folder.children) return;
  for (let childTree of folder.children) {
    const res = getFilePath(childTree, file);
    if (res) return `${fileTree.name}/${res}`;
  }
  return undefined;
};
